import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "../../api";
import { DESK } from "../../constants/testIds";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { formatCurrency } from "../../lib/format";
import { toast } from "../ui/sonner";
import { SubmitButton } from "../common/SubmitButton";
import { useSuccessPulse } from "../../hooks/useSuccessPulse";

// UX-FD-01 §1 SCR-P02-13 "re-join searches existing (incl. lapsed) FIRST, never duplicates":
// this modal is only ever reached via that existing-member path (MemberLookup's Reactivate
// button, or WalkInConversion's 409-MEMBER_EXISTS hand-off) — it calls
// `POST /members/{id}/rejoin`, never `POST /members`.
export function ReactivateModal({ member, open, onOpenChange }) {
  const [planId, setPlanId] = useState("");
  const [start, setStart] = useState(new Date().toISOString().slice(0, 10));
  const queryClient = useQueryClient();
  const [success, pulse] = useSuccessPulse();

  const plansQ = useQuery({
    queryKey: ["desk-plans"],
    queryFn: async () => (await api.get("/plans")).data.plans,
    enabled: open,
  });

  const mutation = useMutation({
    mutationFn: () => api.post(
      `/members/${member.id}/rejoin`, { plan_ref: planId, start },
      { headers: { "Idempotency-Key": crypto.randomUUID() } },
    ),
    onSuccess: () => pulse(() => {
      toast.success(`${member.profile.name} reactivated.`);
      queryClient.invalidateQueries({ queryKey: ["desk-member", member.id] });
      onOpenChange(false);
    }),
    onError: (e) => toast.error(formatApiError(e).message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-modal">
        <DialogHeader>
          <DialogTitle>Reactivate {member?.profile?.name}</DialogTitle>
          <DialogDescription>Pick a plan and start date — this reuses their existing member record, never a duplicate.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Plan <span style={{ color: "var(--scarlet-neg)" }}>*</span></Label>
            <Select onValueChange={setPlanId} value={planId}>
              <SelectTrigger data-testid={DESK.reactivatePlanSelect}><SelectValue placeholder="Choose a plan" /></SelectTrigger>
              <SelectContent>
                {plansQ.data?.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name} — {formatCurrency(p.pricing.amount)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Start date <span style={{ color: "var(--scarlet-neg)" }}>*</span></Label>
            <Input data-testid={DESK.reactivateStartInput} type="date" value={start} onChange={(e) => setStart(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <SubmitButton
            testId={DESK.reactivateSubmitButton} disabled={!planId} pending={mutation.isPending} success={success}
            onClick={() => mutation.mutate()} label="Reactivate" successLabel="Reactivated"
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
